import React, { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

import BreadCrumb from "@/Components/Common/BreadCrumb";
import { Badge, Button, Card, CardBody, CardHeader, Col, Container, Input, Label, Row } from "@/Components/ui";
import { groupReservationService } from "@/api/services/group-reservation.service";
import { MarketerSettlementProfilePayload } from "@/api/types/group-reservation";
import { formatGroupReservationDateRange, getGroupReservationStatusLabel } from "../Reservations/GroupReservationRequests/groupReservationAdmin.mjs";

const emptyProfile = (): MarketerSettlementProfilePayload => ({
  display_name: "",
  contact_phone: "",
  settlement_bank_name: "",
  settlement_account_number: "",
  settlement_account_holder: "",
  settlement_memo: "",
});

const formatAmount = (value?: number | null) => (value ? `${Number(value).toLocaleString()}원` : "0원");

const statusColor = (status: string) => {
  if (status === "approved") return "success";
  if (["reverted", "rejected", "canceled"].includes(status)) return "danger";
  if (["awaiting_bank_transfer", "bank_transfer_confirmed"].includes(status)) return "warning";
  if (status === "reservation_created") return "info";
  return "secondary";
};

export default function MarketerInfoPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [profileForm, setProfileForm] = useState<MarketerSettlementProfilePayload>(emptyProfile());

  const profileQuery = useQuery({
    queryKey: ["group-reservation", "marketer-profile"],
    queryFn: () => groupReservationService.getMarketerProfile(),
  });

  const summaryQuery = useQuery({
    queryKey: ["group-reservation", "marketer-summary"],
    queryFn: () => groupReservationService.getMarketerSummary(),
  });

  const requestQuery = useQuery({
    queryKey: ["group-reservation", "marketer-requests"],
    queryFn: () => groupReservationService.getMarketerRequests(),
  });

  useEffect(() => {
    const profile = profileQuery.data?.data;
    if (!profile) return;
    setProfileForm({
      display_name: profile.display_name || "",
      contact_phone: profile.contact_phone || "",
      settlement_bank_name: profile.settlement_bank_name || "",
      settlement_account_number: profile.settlement_account_number || "",
      settlement_account_holder: profile.settlement_account_holder || "",
      settlement_memo: profile.settlement_memo || "",
    });
  }, [profileQuery.data?.data?.id]);

  const saveProfileMutation = useMutation({
    mutationFn: () =>
      groupReservationService.updateMarketerProfile({
        ...profileForm,
        display_name: profileForm.display_name.trim(),
        contact_phone: profileForm.contact_phone.trim(),
        settlement_bank_name: profileForm.settlement_bank_name.trim(),
        settlement_account_number: profileForm.settlement_account_number.trim(),
        settlement_account_holder: profileForm.settlement_account_holder.trim(),
        settlement_memo: profileForm.settlement_memo?.trim() || null,
      }),
    onSuccess: () => {
      toast.success("대리점 정보를 저장했습니다.");
      queryClient.invalidateQueries({ queryKey: ["group-reservation", "marketer-profile"] });
      queryClient.invalidateQueries({ queryKey: ["group-reservation", "marketer-summary"] });
    },
    onError: (error: any) => toast.error(error?.message || "대리점 정보 저장에 실패했습니다."),
  });

  const summary = summaryQuery.data?.data;
  const profile = profileQuery.data?.data;
  const commissionRateText = profile?.commission_rate ? `${profile.commission_rate}%` : "0.00%";
  const requests = requestQuery.data?.data.results ?? [];

  return (
    <Container fluid>
      <BreadCrumb title="마케터 정보" pageTitle="단체예약" />

      <Row className="g-3">
        <Col xl={4}>
          <Card>
            <CardHeader>대리점 정보</CardHeader>
            <CardBody>
              {profileQuery.isLoading ? (
                <div className="py-5 text-center text-muted">대리점 정보를 불러오는 중입니다.</div>
              ) : profileQuery.isError ? (
                <div className="py-5 text-center text-danger">대리점 정보를 불러오지 못했습니다.</div>
              ) : (
                <>
                  <div className="mb-3">
                    <Label htmlFor="marketer-display-name">표시명</Label>
                    <Input
                      id="marketer-display-name"
                      value={profileForm.display_name}
                      onChange={(event) => setProfileForm((prev) => ({ ...prev, display_name: event.target.value }))}
                    />
                  </div>
                  <div className="mb-3">
                    <Label htmlFor="marketer-contact-phone">연락처</Label>
                    <Input
                      id="marketer-contact-phone"
                      value={profileForm.contact_phone}
                      onChange={(event) => setProfileForm((prev) => ({ ...prev, contact_phone: event.target.value }))}
                    />
                  </div>
                  <Row className="g-3">
                    <Col md={6}>
                      <Label htmlFor="marketer-bank">정산 은행</Label>
                      <Input
                        id="marketer-bank"
                        value={profileForm.settlement_bank_name}
                        onChange={(event) => setProfileForm((prev) => ({ ...prev, settlement_bank_name: event.target.value }))}
                      />
                    </Col>
                    <Col md={6}>
                      <Label htmlFor="marketer-holder">예금주</Label>
                      <Input
                        id="marketer-holder"
                        value={profileForm.settlement_account_holder}
                        onChange={(event) => setProfileForm((prev) => ({ ...prev, settlement_account_holder: event.target.value }))}
                      />
                    </Col>
                  </Row>
                  <div className="mt-3">
                    <Label htmlFor="marketer-account">정산 계좌번호</Label>
                    <Input
                      id="marketer-account"
                      value={profileForm.settlement_account_number}
                      onChange={(event) => setProfileForm((prev) => ({ ...prev, settlement_account_number: event.target.value }))}
                    />
                  </div>
                  <div className="mt-3">
                    <Label>대리점 수수료율(%)</Label>
                    <div className="rounded-md border bg-light px-3 py-2 text-muted fs-14">{commissionRateText}</div>
                  </div>
                  <div className="mt-3">
                    <Label htmlFor="marketer-memo">메모</Label>
                    <Input
                      id="marketer-memo"
                      type="textarea"
                      rows={3}
                      value={profileForm.settlement_memo || ""}
                      onChange={(event) => setProfileForm((prev) => ({ ...prev, settlement_memo: event.target.value }))}
                    />
                  </div>
                  <Button className="mt-4" onClick={() => saveProfileMutation.mutate()} disabled={saveProfileMutation.isPending}>
                    저장
                  </Button>
                </>
              )}
            </CardBody>
          </Card>
        </Col>

        <Col xl={8}>
          <Row className="g-3">
            <Col md={3}>
              <Card>
                <CardBody>
                  <div className="text-muted fs-12">전체 신청</div>
                  <div className="fs-4 fw-bold">{summary?.request_count ?? 0}</div>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card>
                <CardBody>
                  <div className="text-muted fs-12">승인 신청</div>
                  <div className="fs-4 fw-bold">{summary?.approved_request_count ?? 0}</div>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card>
                <CardBody>
                  <div className="text-muted fs-12">승인 금액</div>
                  <div className="fs-5 fw-bold">{formatAmount(summary?.approved_total_amount)}</div>
                </CardBody>
              </Card>
            </Col>
            <Col md={3}>
              <Card>
                <CardBody>
                  <div className="text-muted fs-12">예상 수수료</div>
                  <div className="fs-5 fw-bold">{formatAmount(summary?.estimated_commission_amount)}</div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          <Card>
            <CardHeader className="d-flex flex-wrap align-items-center justify-content-between gap-2">
              <span>내 단체예약 신청</span>
              <Button color="dark" size="sm" onClick={() => navigate("/group-reservation-requests/new")}>
                신청 등록
              </Button>
            </CardHeader>
            <CardBody>
              {requestQuery.isLoading ? (
                <div className="py-5 text-center text-muted">신청 목록을 불러오는 중입니다.</div>
              ) : requestQuery.isError ? (
                <div className="py-5 text-center text-danger">신청 목록을 불러오지 못했습니다.</div>
              ) : requests.length === 0 ? (
                <div className="py-5 text-center text-muted">내 대리점 링크로 접수된 신청이 없습니다.</div>
              ) : (
                <div className="table-responsive">
                  <table className="table align-middle table-nowrap mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>접수번호</th>
                        <th>희망일</th>
                        <th>단체명</th>
                        <th>상태</th>
                        <th>예상금액</th>
                        <th>수수료</th>
                      </tr>
                    </thead>
                    <tbody>
                      {requests.map((request) => (
                        <tr key={request.id}>
                          <td>{request.request_code}</td>
                          <td>{formatGroupReservationDateRange(request)}</td>
                          <td>
                            <div className="fw-semibold">{request.group_name}</div>
                            <div className="text-muted fs-12">{request.applicant_phone}</div>
                          </td>
                          <td>
                            <Badge color={statusColor(request.status)}>
                              {getGroupReservationStatusLabel(request.status)}
                            </Badge>
                          </td>
                          <td>{formatAmount(request.estimated_total_amount)}</td>
                          <td>
                            <div>{formatAmount(request.agency_commission_amount)}</div>
                            <div className="text-muted fs-12">
                              {request.agency_commission_rate_snapshot ? `${request.agency_commission_rate_snapshot}%` : "-"}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>
    </Container>
  );
}
